import type { ComponentPropsWithoutRef, ComponentType, ReactElement } from 'react';

import classNames from 'classnames';
import { NON_BREAKING_SPACE } from 'utils/constants/common';

import BlokoLink from 'bloko/blocks/link';

import useTrls from 'hooks/translation/useTrls';

import useSliceRowItems from 'components/CampaignCard/CampaignInfo/Targetings/utils/useSliceRowItems';

import styles from 'components/CampaignCard/CampaignInfo/Targetings/utils/WithAdditional.module.less';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

type ComponentProps<T extends ComponentType> = ComponentPropsWithoutRef<T> & {
    items: Array<string>;
    titleTrl: string;
    onAdditionalTextClick?: () => unknown;
};

interface BaseComponent<T extends ComponentType> {
    baseComponent: T;
}

type AdditionalProps<T extends ComponentType> = ComponentProps<T> & BaseComponent<T>;

const Additional = <T extends ComponentType>({
    baseComponent,
    onAdditionalTextClick,
    ...rest
}: AdditionalProps<T>): ReactElement => {
    const trls = useTrls();
    const Component = baseComponent as ComponentType<Record<string, unknown>>;
    const { collapsedCount, isOverflow, stringItems } = useSliceRowItems(rest.titleTrl.length, rest.items);

    if (collapsedCount) {
        return (
            <section className={classNames(styles.wrapper)}>
                <div title={stringItems} className={classNames({ [styles.textOverflow]: isOverflow })}>
                    <Component {...rest} />
                </div>
                {`,${NON_BREAKING_SPACE}`}
                <BlokoLink onClick={onAdditionalTextClick}>{`${trls(
                    'campaign.targetings.additional.item'
                )}${NON_BREAKING_SPACE}${collapsedCount}`}</BlokoLink>
            </section>
        );
    }

    return (
        <section className={classNames({ [styles.textOverflow]: isOverflow })}>
            <Component {...rest} />
        </section>
    );
};

const buildAdditional = <T extends AnyComponent>(Component: T, props: ComponentProps<T>): ReactElement => (
    <Additional {...props} baseComponent={Component} />
);

const WithAdditional = <T extends AnyComponent>(Component: T): ((props: ComponentProps<T>) => ReactElement) =>
    buildAdditional.bind(null, Component);

export default WithAdditional;
